import {t} from '@lingui/macro';
import {ReportKey} from '../../enums/ReportKey';
import { getStoreExternalUrl } from '../../routers/StoreRouter/helpers/getStoreExternalUrl';
import {TransactionUrl} from '../../redux/Transaction/helpers/TransactionUrl';
import {Type} from '../../redux/Transaction/helper';

/**
 * SIDEBAR KEYS
 * - key                : String    - route to page
 * - label              : String    - title to display in sidebar
 * - leftIcon           : String
 *
 * GLOBAL SEARCH KEYS
 * - globalSearchLabel     : String - to prevent naming conflicts or to give a more distinctive name
 *                                     - eg: 'Payments' --> 'Invoice Payments' / 'Bill Payments'
 *                                     - eg: 'Items'    --> 'Product Items'
 * - globalSearchBaseLabel : String - To support searching by English when UI is in non-English.
 *                                     - the value is label or globalSearchLabel without t``
 * - globalSearchChildren  : []     - routes that are not in sidebar, but still searchable (eg: New / Batch)
 * - globalSearchKeywords  : []     - Attach more search terms to a page,
 *                                      - queryString key in object to support query param
 *                                      - eg: {searchLabel: t`Signature`, queryString: {tab: 'signature'}}
 *                                      - /cp/settings?tab=signature --- searched using 'Signature'
 * @returns
 */
export const getSidebarOptions = streams => [
    {
        key: '/dashboard',
        label: t`Dashboard`,
        globalSearchBaseLabel: 'Dashboard',
        leftIcon: 'bar-chart',
    },
    ...(!process.env.REACT_APP_SHOW_AI || process.env.REACT_APP_SHOW_AI === "false") ? [] : [{
        key: '/bukku-ai',
        label: t`Bukku AI`,
        globalSearchBaseLabel: 'Bukku AI',
        leftIcon: 'robot',
        isPreview: true,
    }],
    {
        key: '/financing-portal',
        label: t`Financing Portal`,
        globalSearchBaseLabel: 'Financing Portal',
        leftIcon: 'dollar',
    },
    {
        key: '/sales',
        label: t`Sales`,
        globalSearchBaseLabel: 'Sales',
        leftIcon: 'plus-square',
        children: [
            {
                key: TransactionUrl.list(Type.sale_quote),
                label: t`Quotations`,
                globalSearchBaseLabel: 'Quotations',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.sale_quote),
                        label: t`New Quotation`,
                        globalSearchBaseLabel: 'New Quotation',
                    },
                    {
                        key: TransactionUrl.batch(Type.sale_quote),
                        label: t`Batch Quotations`,
                        globalSearchBaseLabel: 'Batch Quotations',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.sale_order),
                label: t`Sale Orders`,
                globalSearchBaseLabel: 'Sale Orders',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.sale_order),
                        label: t`New Sales Order`,
                        globalSearchBaseLabel: 'New Sales Order',
                    },
                    {
                        key: TransactionUrl.batch(Type.sale_quote),
                        label: t`Batch Sales Orders`,
                        globalSearchBaseLabel: 'Batch Sales Orders',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.sale_delivery_order),
                label: t`Delivery Orders`,
                globalSearchBaseLabel: 'Delivery Orders',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.sale_delivery_order),
                        label: t`New Delivery Order`,
                        globalSearchBaseLabel: 'New Delivery Order',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.sale_invoice),
                label: t`Invoices`,
                globalSearchBaseLabel: 'Invoices',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.sale_invoice),
                        label: t`New Invoice`,
                        globalSearchBaseLabel: 'New Invoice',
                    },
                    {
                        key: TransactionUrl.batch(Type.sale_invoice),
                        label: t`Batch Invoices`,
                        globalSearchBaseLabel: 'Batch Invoices',
                    },
                    {
                        key: '/sales/invoices/batch-cash',
                        label: t`Batch Cash Invoices`,
                        globalSearchBaseLabel: 'Batch Cash Invoices',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.sale_credit_note),
                label: t`Credit Notes`,
                globalSearchBaseLabel: 'Credit Notes',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.sale_credit_note),
                        label: t`New Credit Note`,
                        globalSearchBaseLabel: 'New Credit Note',
                    },
                    {
                        key: TransactionUrl.batch(Type.sale_credit_note),
                        label: t`Batch Credit Notes`,
                        globalSearchBaseLabel: 'Batch Credit Notes',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.sale_payment),
                label: t`Payments`,
                globalSearchLabel: t`Invoice Payments`,
                globalSearchBaseLabel: 'Invoice Payments',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.sale_payment),
                        label: t`New Payment`,
                        globalSearchBaseLabel: 'New Payment',
                    },
                    {
                        key: TransactionUrl.batch(Type.sale_payment),
                        label: t`Batch Payments`,
                        globalSearchBaseLabel: 'Batch Payments',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.sale_refund),
                label: t`Refunds`,
                globalSearchLabel: t`Invoice Refunds`,
                globalSearchBaseLabel: 'Invoice Refunds',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.sale_refund),
                        label: t`New Refunds`,
                        globalSearchBaseLabel: 'New Refunds',
                    },
                ],
            },
        ],
    },
    {
        key: '/purchases',
        label: t`Purchases`,
        globalSearchBaseLabel: 'Purchases',
        leftIcon: 'minus-square',
        children: [
            {
                key: TransactionUrl.list(Type.purchase_order),
                label: t`Purchase Orders`,
                globalSearchBaseLabel: 'Purchase Orders',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.purchase_order),
                        label: t`New Purchase Order`,
                        globalSearchBaseLabel: 'New Purchase Order',
                    },
                    {
                        key: TransactionUrl.batch(Type.purchase_order),
                        label: t`Batch Purchase Orders`,
                        globalSearchBaseLabel: 'Batch Purchase Orders',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.purchase_goods_received_note),
                label: t`Goods Received Notes`,
                globalSearchBaseLabel: 'Goods Received Notes',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.purchase_goods_received_note),
                        label: t`New Goods Received Note`,
                        globalSearchBaseLabel: 'New Goods Received Note',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.purchase_bill),
                label: t`Bills`,
                globalSearchBaseLabel: 'Bills',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.purchase_bill),
                        label: t`New Bill`,
                        globalSearchBaseLabel: 'New Bill',
                    },
                    {
                        key: TransactionUrl.batch(Type.purchase_bill),
                        label: t`Batch Bills`,
                        globalSearchBaseLabel: 'Batch Bills',
                    },
                    {
                        key: '/purchases/bills/batch-cash',
                        label: t`Batch Cash Bills`,
                        globalSearchBaseLabel: 'Batch Cash Bills',
                    },
                    {
                        key: '/purchases/bills/batch-expense-claims',
                        label: t`Batch Expense Claims`,
                        globalSearchBaseLabel: 'Batch Expense Claims',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.purchase_credit_note),
                label: t`Credit Notes`,
                globalSearchLabel: t`Purchase Credit Notes`,
                globalSearchBaseLabel: 'Purchase Credit Notes',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.purchase_credit_note),
                        label: t`New Purchase Credit Note`,
                        globalSearchBaseLabel: 'New Purchase Credit Note',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.purchase_payment),
                label: t`Payments`,
                globalSearchLabel: t`Purchase Payments`,
                globalSearchBaseLabel: 'Purchase Payments',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.purchase_payment),
                        label: t`New Purchase Payment`,
                        globalSearchBaseLabel: 'New Purchase Payment',
                    },
                    {
                        key: TransactionUrl.batch(Type.purchase_payment),
                        label: t`Batch Purchase Payments`,
                        globalSearchBaseLabel: 'Batch Purchase Payments',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.purchase_refund),
                label: t`Refunds`,
                globalSearchLabel: t`Purchase Refunds`,
                globalSearchBaseLabel: 'Purchase Refunds',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.purchase_refund),
                        label: t`New Purchase Refund`,
                        globalSearchBaseLabel: 'New Purchase Refund',
                    },
                ],
            },
        ],
    },
    {
        key: '/shoebox',
        label: t`Digital Shoebox`,
        globalSearchBaseLabel: 'Digital Shoebox',
        leftIcon: 'inbox',
    },
    {
        key: '/bank',
        label: t`Bank`,
        globalSearchBaseLabel: 'Bank',
        leftIcon: 'bank',
        children: [
            {
                key: TransactionUrl.list(Type.bank_income),
                label: t`Money In`,
                globalSearchBaseLabel: 'Money In',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.bank_income),
                        label: t`New Money In`,
                        globalSearchBaseLabel: 'New Money In',
                    },
                    {
                        key: TransactionUrl.batch(Type.bank_income),
                        label: t`Batch Money In`,
                        globalSearchBaseLabel: 'Batch Money In',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.bank_expense),
                label: t`Money Out`,
                globalSearchBaseLabel: 'Money Out',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.bank_expense),
                        label: t`New Money Out`,
                        globalSearchBaseLabel: 'New Money Out',
                    },
                    {
                        key: TransactionUrl.batch(Type.bank_expense),
                        label: t`Batch Money Out`,
                        globalSearchBaseLabel: 'Batch Money Out',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.bank_transfer),
                label: t`Transfers`,
                globalSearchBaseLabel: 'Bank Transfers',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.bank_transfer),
                        label: t`New Transfer`,
                        globalSearchBaseLabel: 'New Bank Transfer',
                    },
                ],
            },
            {
                key: '/bank/accounts',
                label: t`Accounts`,
                globalSearchBaseLabel: 'Accounts',
                globalSearchKeywords: [
                    { searchLabel: t`Bank Reconciliation` },
                    { searchLabel: t`Manual Reconciliation` },
                ],
                globalSearchChildren: [
                    {
                        key: '/bank/accounts/new',
                        label: t`New Account`,
                        globalSearchBaseLabel: 'New Bank Account',
                    },
                    {
                        key: '/bank/feeds',
                        label: t`Bank Feeds`,
                        globalSearchBaseLabel: 'Bank Feeds',
                        globalSearchKeywords: [
                            { searchLabel: t`Maybank` },
                            { searchLabel: t`Connect your bank` },
                        ],
                    },
                ],
            },
        ],
    },
    {
        key: '/contacts',
        label: t`Contacts`,
        globalSearchBaseLabel: 'Contacts',
        leftIcon: 'contacts',
        globalSearchKeywords: [
            { searchLabel: t`Customers`, queryString: {type: 'customer'} },
            { searchLabel: t`Suppliers`, queryString: {type: 'supplier'} },
            { searchLabel: t`Employee`, queryString: {type: 'employee'} },
            { searchLabel: t`Statement of Account` },
        ],
        globalSearchChildren: [
            {
                key: '/contacts/new',
                label: t`New Contact`,
                globalSearchBaseLabel: 'New Contact',
            },
            {
                key: '/contacts/import',
                label: t`Import Contacts`,
                globalSearchBaseLabel: 'Import Contacts',
            },
            {
                key: '/contacts/batch-update',
                label: t`Batch Update Contacts`,
                globalSearchBaseLabel: 'Batch Update Contacts',
            },
            {
                key: '/contacts/groups',
                label: t`Contact Groups`,
                globalSearchBaseLabel: 'Contact Groups',
            },
            {
                key: '/contacts/groups/new',
                label: t`New Contact Group`,
                globalSearchBaseLabel: 'New Contact Group',
            },
            // {
            //     key: '/contacts',
            //     label: t`Statement of Account`,
            // },
        ],
    },
    {
        key: '/products',
        label: t`Products & Services`,
        leftIcon: 'code-sandbox',
        globalSearchBaseLabel: 'Product Items',
        globalSearchChildren: [
            {
                key: '/products/new',
                label: t`New Product`,
                globalSearchBaseLabel: 'New Product',
            },
            {
                key: '/products/import',
                label: t`Import Products`,
                globalSearchBaseLabel: 'Import Products',
            },
            {
                key: '/products/batch-update',
                label: t`Batch Update Products`,
                globalSearchBaseLabel: 'Batch Update Products',
            },
            {
                key: '/products/groups',
                label: t`Product Groups`,
            },
            {
                key: '/products/groups/new',
                label: t`New Product Group`,
            },
        ],
    },
    {
        key: '/stock',
        label: t`Stock`,
        leftIcon: 'code-sandbox',
        children: [
            {
                key: TransactionUrl.list(Type.stock_adjustment),
                label: t`Adjustments`,
                globalSearchBaseLabel: 'Stock Adjustments',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.stock_adjustment),
                        label: t`New Adjustment`,
                        globalSearchBaseLabel: 'New Stock Adjustment',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.stock_transfer),
                label: t`Transfers`,
                globalSearchBaseLabel: 'Stock Transfers',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.stock_transfer),
                        label: t`New Transfer`,
                        globalSearchBaseLabel: 'New Stock Transfer',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.stock_value),
                label: t`Periodic Values`,
            },
        ],
    },
    {
        key: '/myinvois',
        label: t`MyInvois`,
        globalSearchBaseLabel: 'MyInvois',
        leftIcon: 'myinvois',
        isNew: true,
        children: [
            {
                key: '/myinvois/transactions',
                label: t`Bukku Transactions`,
                globalSearchBaseLabel: 'MyInvois Bukku Transactions',
            },
            {
                key: '/myinvois/minipos',
                label: t`MiniPOS Orders`,
                globalSearchBaseLabel: 'MyInvois MiniPOS Orders',
            },
            {
                key: '/myinvois/documents',
                label: t`MyInvois Documents`,
                globalSearchBaseLabel: 'MyInvois Documents',
            },
            ...(!process.env.REACT_APP_SHOW_MYINVOIS_INCOMING_DOCUMENTS || process.env.REACT_APP_SHOW_MYINVOIS_INCOMING_DOCUMENTS === "false") ? [] : [{
                key: '/myinvois/incoming-documents',
                label: t`Incoming Documents`,
                globalSearchBaseLabel: 'Incoming Documents',
            }],
        ],
    },
    {
        key: '/reports',
        label: t`Reports`,
        globalSearchBaseLabel: 'Reports',
        leftIcon: 'file-text',
        children: [
            // Financial Reports
            {
                key: ReportKey.balance_sheet,
                label: t`Balance Sheet`,
                globalSearchBaseLabel: 'Balance Sheet',
            },
            {
                key: ReportKey.profit_loss,
                label: t`Profit & Loss`,
            },
            {
                key: ReportKey.cash_flow,
                label: t`Cash Flow Statement`,
                globalSearchBaseLabel: 'Cash Flow Statement',
            },
            {
                key: ReportKey.financial_summary,
                label: t`Financial Summary`,
                globalSearchBaseLabel: 'Financial Summary',
            },
            // Aging Reports
            {
                key: ReportKey.aged_receivables_summary,
                label: t`Aged Receivables Summary`,
                globalSearchBaseLabel: 'Aged Receivables Summary',
            },
            {
                key: ReportKey.aged_receivables_detail,
                label: t`Aged Receivables Detail`,
                globalSearchBaseLabel: 'Aged Receivables Detail',
            },
            {
                key: ReportKey.aged_payables_summary,
                label: t`Aged Payables Summary`,
                globalSearchBaseLabel: 'Aged Payables Summary',
            },
            {
                key: ReportKey.aged_payables_detail,
                label: t`Aged Payables Detail`,
                globalSearchBaseLabel: 'Aged Payables Detail',
            },
            // Sales Reports
            {
                key: ReportKey.invoice_summary,
                label: t`Invoice Summary`,
                globalSearchBaseLabel: 'Invoice Summary',
            },
            {
                key: ReportKey.credit_note_summary,
                label: t`Sales Credit Note Summary`,
                globalSearchBaseLabel: 'Sales Credit Note Summary',
            },
            {
                key: ReportKey.payment_summary,
                label: t`Sales Payment Summary`,
                globalSearchBaseLabel: 'Sales Payment Summary',
            },
            {
                key: ReportKey.customer_quotes,
                label: t`Quotations by Customer`,
                globalSearchBaseLabel: 'Quotations by Customer',
            },
            {
                key: ReportKey.customer_sales_orders,
                label: t`Sales Orders By Customer`,
                globalSearchBaseLabel: 'Sales Orders By Customer',
            },
            {
                key: ReportKey.customer_delivery_orders,
                label: t`Delivery Orders By Customer`,
                globalSearchBaseLabel: 'Delivery Orders By Customer',
            },
            {
                key: ReportKey.customer_sales_summary,
                label: t`Sales Summary By Customer`,
                globalSearchBaseLabel: 'Sales Summary By Customer',
            },
            {
                key: ReportKey.tag_sales_summary,
                label: t`Sales By Tag`,
                globalSearchBaseLabel: 'Sales By Tag',
            },

            // Purchase Reports
            {
                key: ReportKey.bill_summary,
                label: t`Bill Summary`,
                globalSearchBaseLabel: 'Bill Summary',
            },
            {
                key: ReportKey.purchase_credit_note_summary,
                label: t`Purchase Credit Note Summary`,
                globalSearchBaseLabel: 'Purchase Credit Note Summary',
            },
            {
                key: ReportKey.purchase_payment_summary,
                label: t`Purchase Payment Summary`,
                globalSearchBaseLabel: 'Purchase Payment Summary',
            },
            {
                key: ReportKey.supplier_orders,
                label: t`Purchase Orders by Supplier`,
                globalSearchBaseLabel: 'Purchase Orders by Supplier',
            },
            {
                key: ReportKey.supplier_purchases_summary,
                label: t`Purchase Summary by Supplier`,
                globalSearchBaseLabel: 'Purchase Summary by Supplier',
            },

            // Product Reports
            {
                key: ReportKey.product_sales_summary,
                label: t`Product Sales Summary`,
                globalSearchBaseLabel: 'Product Sales Summary',
            },
            {
                key: ReportKey.product_sales_detail,
                label: t`Product Sales Detail`,
                globalSearchBaseLabel: 'Product Sales Detail',
            },
            {
                key: ReportKey.product_bundle_sales_summary,
                label: t`Product Bundle Sales Summary`,
                globalSearchBaseLabel: 'Product Bundle Sales Summary',
                isNew: true,
            },
            {
                key: ReportKey.product_purchases_summary,
                label: t`Product Purchase Summary`,
                globalSearchBaseLabel: 'Product Purchase Summary',
            },
            {
                key: ReportKey.product_purchases_detail,
                label: t`Product Purchase Detail`,
                globalSearchBaseLabel: 'Product Purchase Detail',
            },
            {
                key: ReportKey.item_sales_collection,
                label: t`Item Sales Collection`,
                globalSearchBaseLabel: 'Item Sales Collection',
            },

            // Inventory Reports
            {
                key: ReportKey.inventory_summary,
                label: t`Inventory Summary`,
                globalSearchBaseLabel: 'Inventory Summary',
            },
            {
                key: ReportKey.inventory_detail,
                label: t`Inventory Detail`,
                globalSearchBaseLabel: 'Inventory Detail',
            },
            {
                key: ReportKey.inventory_summary_by_location,
                label: t`Inventory Summary by Location`,
                globalSearchBaseLabel: 'Inventory Summary by Location',
            },
            {
                key: ReportKey.profit_summary,
                label: t`Profit Summary`,
                globalSearchBaseLabel: 'Profit Summary',
            },
            {
                key: ReportKey.profit_detail,
                label: t`Profit Detail`,
                globalSearchBaseLabel: 'Profit Detail',
                isNew: true,
            },

            // Fixed Asset Reports
            {
                key: ReportKey.fixed_assets,
                label: t`Fixed Asset List`,
                globalSearchBaseLabel: 'Fixed Asset List',
            },
            {
                key: ReportKey.depreciation_schedule,
                label: t`Depreciation Schedule`,
                globalSearchBaseLabel: 'Depreciation Schedule',
            },
            {
                key: ReportKey.disposal_schedule,
                label: t`Disposal Schedule`,
                globalSearchBaseLabel: 'Disposal Schedule',
            },

            // Ledger Reports
            {
                key: ReportKey.general_ledger,
                label: t`General Ledger`,
                globalSearchBaseLabel: 'General Ledger',
            },
            {
                key: ReportKey.debtor_ledger,
                label: t`Debtor Ledger`,
                globalSearchBaseLabel: 'Debtor Ledger',
            },
            {
                key: ReportKey.creditor_ledger,
                label: t`Creditor Ledger`,
                globalSearchBaseLabel: 'Creditor Ledger',
            },

            // SST Reports
            {
                key: ReportKey.sst_02,
                label: t`SST-02`,
                globalSearchBaseLabel: 'SST-02',
            },
            {
                key: ReportKey.sst_sales_detail,
                label: t`SST Sales Detail`,
                globalSearchBaseLabel: 'SST Sales Detail',
            },
            {
                key: ReportKey.sst_purchase_detail,
                label: t`SST Purchase Detail`,
                globalSearchBaseLabel: 'SST Purchase Detail',
            },
            {
                key: ReportKey.sst_payment_collection,
                label: t`SST Payment Collection`,
                globalSearchBaseLabel: 'SST Payment Collection',
            },
            {
                key: ReportKey.sst_deemed_payments,
                label: t`SST Deemed Payments`,
                globalSearchBaseLabel: 'SST Deemed Payments',
            },
            {
                key: ReportKey.sst_recovered_payments,
                label: t`SST Recovered Payments`,
                globalSearchBaseLabel: 'SST Recovered Payments',
            },
            {
                key: ReportKey.sst_by_products,
                label: t`SST by Products`,
                globalSearchBaseLabel: 'SST by Products',
            },

            // Accounting Reports
            {
                key: ReportKey.trial_balance,
                label: t`Trial Balance`,
                globalSearchBaseLabel: 'Trial Balance',
            },
            {
                key: ReportKey.bank_reconciliation,
                label: t`Bank Reconciliation`,
                globalSearchBaseLabel: 'Bank Reconciliation',
            },
            {
                key: ReportKey.reconciled_transactions,
                label: t`Reconciled Transactions`,
                globalSearchBaseLabel: 'Reconciled Transactions',
            },
            {
                key: ReportKey.transaction_list,
                label: t`Transaction List`,
                globalSearchBaseLabel: 'Transaction List',
            },
            {
                key: ReportKey.foreign_exchange_summary,
                label: t`Exchange Gain/Loss Summary`,
            },
            // {
            //     key: ReportKey.foreign_exchange_detail,
            //     label: t`Exchange Gain/Loss Detail`,
            //     isNew: true,
            // },
            {
                key: ReportKey.unrealised_exchange_detail,
                label: t`Unrealised Exchange Detail`,
                globalSearchBaseLabel: 'Unrealised Exchange Detail',
            },
            {
                key: ReportKey.exchange_rates,
                label: t`Exchange Rates`,
                globalSearchBaseLabel: 'Exchange Rates',
            },
            {
                key: ReportKey.audit_trail,
                label: t`Audit Trail`,
                globalSearchBaseLabel: 'Audit Trail',
            },
            {
                key: ReportKey.payment_voucher_summary,
                label: t`Payment Voucher Summary`,
                globalSearchBaseLabel: 'Payment Voucher Summary',
            },
            {
                key: ReportKey.official_receipt_summary,
                label: t`Official Receipt Summary`,
                globalSearchBaseLabel: 'Official Receipt Summary',
            },
            {
                key: ReportKey.cash_sales_summary,
                label: t`Cash Sales Summary`,
                globalSearchBaseLabel: 'Cash Sales Summary',
                isNew: true,
            },
            {
                key: ReportKey.cash_purchase_summary,
                label: t`Cash Purchase Summary`,
                globalSearchBaseLabel: 'Cash Purchase Summary',
                isNew: true,
            },
            {
                key: ReportKey.journal_entry_detail,
                label: t`Journal Entry Detail`,
                globalSearchBaseLabel: 'Journal Entry Detail',
            },
            {
                key: ReportKey.double_entry_detail,
                label: t`Double Entry Detail`,
                globalSearchBaseLabel: 'Double Entry Detail',
            },
            {
                key: ReportKey.chart_of_accounts,
                label: t`Chart of Accounts`,
                globalSearchLabel: t`Chart of Accounts Report`,
                globalSearchBaseLabel: 'Chart of Accounts Report',
            },
            {
                key: '/reports/all',
                label: t`All Reports`,
                globalSearchBaseLabel: 'All Reports',
            },
        ],
    },
    {
        key: '/accounting',
        label: t`Accounting`,
        globalSearchBaseLabel: 'Accounting',
        leftIcon: 'book',
        children: [
            {
                key: TransactionUrl.list(Type.journal_entry),
                label: t`Journal Entries`,
                globalSearchBaseLabel: 'Journal Entries',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.journal_entry),
                        label: t`New Journal Entry`,
                        globalSearchBaseLabel: 'New Journal Entry',
                    },
                    {
                        key: TransactionUrl.batch(Type.journal_entry),
                        label: t`Batch Journal Entries`,
                        globalSearchBaseLabel: 'Batch Journal Entries',
                    },
                    {
                        key: '/accounting/journal-entries/payroll',
                        label: t`Import Payroll File`,
                        globalSearchBaseLabel: 'Import Payroll File',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.contra),
                label: t`Contras`,
                globalSearchBaseLabel: 'Contras',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.contra),
                        label: t`New Contra`,
                        globalSearchBaseLabel: 'New Contra',
                    },
                ],
            },
            {
                key: '/accounting/assets',
                label: t`Fixed Assets`,
                globalSearchBaseLabel: 'Fixed Assets',
                globalSearchChildren: [
                    {
                        key: '/accounting/assets/new',
                        label: t`New Asset`,
                        globalSearchBaseLabel: 'New Asset',
                    },
                    {
                        key: '/accounting/assets/batch',
                        label: t`Batch Register Assets`,
                        globalSearchBaseLabel: 'Batch Register Assets',
                    },
                    {
                        key: '/accounting/assets/depreciate',
                        label: t`Run Asset Depreciation`,
                        globalSearchBaseLabel: 'Run Asset Depreciation',
                    },
                ],
            },
            {
                key: TransactionUrl.list(Type.tax_deemed_payment),
                label: t`Deemed Payments`,
                globalSearchBaseLabel: 'Deemed Payments',
                globalSearchChildren: [
                    {
                        key: TransactionUrl.new(Type.tax_deemed_payment),
                        label: t`New Deemed Payment`,
                        globalSearchBaseLabel: 'New Deemed Payment',
                    },
                ],
            },
            {
                key: '/accounting/accounts',
                label: t`Chart of Accounts`,
                globalSearchBaseLabel: 'Chart of Accounts',
                globalSearchChildren: [
                    {
                        key: '/accounting/accounts/new',
                        label: t`New Account`,
                        globalSearchBaseLabel: 'New Account',
                    },
                    {
                        key: '/accounting/accounts/import',
                        label: t`Import Accounts`,
                        globalSearchBaseLabel: 'Import Accounts',
                    },
                    {
                        key: '/accounting/accounts/opening-balance',
                        label: t`Opening Balance`,
                        globalSearchBaseLabel: 'Opening Balance',
                    },
                ],
            },
        ],
    },
    {
        key: '/flow',
        label: t`Flow`,
        globalSearchBaseLabel: 'Flow',
        leftIcon: 'flow',
        isNew: true,
        children: [
            ...streams ? streams.reduce((activeStreams, stream) => {
                activeStreams.push({
                    key: `/flow/streams/${stream.id}/digests`,
                    label: t`${stream.ext_name}`,
                    globalSearchBaseLabel: stream.ext_name,
                    globalSearchKeywords: [
                        {searchLabel: t`Digests`},
                    ],
                });
                return activeStreams;
            }, []) : [],
        ],
    },
    {
        key: '/cp',
        label: t`Control Panel`,
        globalSearchBaseLabel: 'Control Panel',
        leftIcon: 'control',
        children: [
            {
                key: '/cp/files',
                label: t`File Manager`,
            },
            {
                key: '/cp/apps',
                label: t`Apps`,
                isNew: true,
            },
            {
                key: '/cp/instructions',
                label: t`Recurring Instructions`,
                globalSearchBaseLabel: 'Recurring Instructions',
                globalSearchChildren: [
                    {
                        key: '/cp/instructions/new',
                        label: t`New Recurring Instruction`,
                        globalSearchBaseLabel: 'New Recurring Instruction',
                    },
                ],
            },
            {
                key: '/cp/terms',
                label: t`Payment Terms`,
                globalSearchBaseLabel: 'Payment Terms',
                globalSearchChildren: [
                    {
                        key: '/cp/terms/new',
                        label: t`New Payment Term`,
                        globalSearchBaseLabel: 'New Payment Term',
                    },
                ],
            },
            {
                key: '/cp/payment-methods',
                label: t`Payment Methods`,
                globalSearchBaseLabel: 'Payment Methods',
            },
            {
                key: '/cp/currencies',
                label: t`Currencies`,
                globalSearchBaseLabel: 'Currencies',
                globalSearchKeywords: [{ searchLabel: t`Custom Rates`}],
            },
            // {
            //   key: '/contacts/groups',
            //   label: t`Contact Groups`,
            // },
            {
                key: '/cp/tags',
                label: t`Tags`,
                globalSearchBaseLabel: 'Tags',
            },
            {
                key: '/cp/asset-types',
                label: t`Asset Types`,
                globalSearchBaseLabel: 'Asset Types',
                globalSearchChildren: [
                    {
                        key: '/cp/asset-types/new',
                        label: t`New Asset Type`,
                        globalSearchBaseLabel: 'New Asset Type',
                    },
                ],
            },
            {
                key: '/cp/locations',
                label: t`Locations`,
                globalSearchBaseLabel: 'Locations',
                globalSearchChildren: [
                    {
                        key: '/cp/locations/new',
                        label: t`New Location`,
                        globalSearchBaseLabel: 'New Location',
                    },
                ],
            },
            {
                key: '/cp/fields',
                label: t`Custom Fields`,
                globalSearchBaseLabel: 'Custom Fields',
                globalSearchChildren: [
                    {
                        key: '/cp/fields/new',
                        label: t`New Custom Field`,
                        globalSearchBaseLabel: 'New Custom Field',
                    },
                ],
            },
            {
                key: '/cp/price-levels',
                label: t`Price Levels`,
                globalSearchBaseLabel: 'Price Levels',
            },
            {
                key: '/cp/number-formats',
                label: t`Number Formats`,
                globalSearchBaseLabel: 'Number Formats',
            },
            {
                key: '/cp/form-designs',
                label: t`Form Designs`,
                globalSearchBaseLabel: 'Form Designs',
                globalSearchChildren: [
                    {
                        key: '/cp/form-designs/new',
                        label: t`New Form Design`,
                        globalSearchBaseLabel: 'New Form Design',
                    },
                ],
            },
            {
                key: '/cp/users',
                label: t`Users`,
                globalSearchBaseLabel: 'Users',
                globalSearchKeywords: [{searchLabel: t`Permissions`}, {searchLabel: t`Special Rights`}],
                globalSearchChildren: [
                    {
                        key: '/cp/users/new',
                        label: t`Invite User`,
                        globalSearchBaseLabel: 'Invite User',
                    },
                ],
            },
            {
                key: '/cp/profile',
                label: t`Company Profile`,
                globalSearchBaseLabel: 'Company Profile',
                globalSearchKeywords: [{searchLabel: t`Addresses`}],
            },
            {
                key: '/cp/settings',
                label: t`Company Settings`,
                globalSearchBaseLabel: 'Company Settings',
                globalSearchKeywords: [
                    {searchLabel: t`Tax`, queryString: {tab: 'tax'}},
                    {searchLabel: t`Form Designs`, queryString: {tab: 'design'}},
                    {searchLabel: t`Number Formats`, queryString: {tab: 'number_formats'}},
                    {searchLabel: t`Remarks`, queryString: {tab: 'remarks'}},
                    {searchLabel: t`Signature`, queryString: {tab: 'signature'}},
                    {searchLabel: t`Payment Gateway`, queryString: {tab: 'payment_gateway'}},
                    {searchLabel: t`Invoice Reminder`, queryString: {tab: 'invoice_reminder'}},
                    {searchLabel: t`QuickShare Invoice`, queryString: {tab: 'quickshare_invoice'}},
                    {searchLabel: t`QuickShare Statement`, queryString: {tab: 'quickshare_statement'}},
                ],
                // globalSearchKeywords: [
                //     "Tax", "Form Designs", "Number Formats", "Remarks", "Signature",
                //     "E-Invoicing", "Payment Gateway", "Invoice Reminder", "E-Statement",
                // ],
            },
            {
                key: '/cp/flow',
                label: t`Flow Streams`,
                globalSearchBaseLabel: 'Flow Streams',
                globalSearchKeywords: [
                    // {searchLabel: t`Lazada`},
                    // {searchLabel: t`Storehub`},
                    {searchLabel: t`Streams`},
                ],
            },
            {
                key: '/cp/shoebox',
                label: t`Shoebox Settings`,
                globalSearchBaseLabel: 'Shoebox Settings',
            },
            {
                key: '/cp/subscription',
                label: t`Billing & Subscription`,
                globalSearchKeywords: [{searchLabel: t`Plan`}, {searchLabel: t`Usages`}],
            },
            {
                key: '/cp/integrations',
                label: t`Integrations`,
                globalSearchBaseLabel: 'Integrations',
                globalSearchKeywords: [
                    {searchLabel: t`API Access`, queryString: {tab: 'api'} },
                    // {searchLabel: t`Shopee Settings`, queryString: {tab: 'shopee'} },
                ],
            },
            {
                key: '/cp/backup-data',
                label: t`Backup Data`,
                globalSearchBaseLabel: 'Backup Data',
            },
        ],
    },
    {
        key: '/cp-store',
        label: t`Bukku Store`,
        globalSearchBaseLabel: 'Bukku Store',
        leftIcon: 'store',
        children: [
            {
                key: '/cp-store/orders',
                label: t`Orders`,
                globalSearchLabel: t`Bukku Store Orders`,
                globalSearchBaseLabel: 'Bukku Store Orders',
            },
            {
                key: '/cp-store/categories',
                label: t`Store Catalogue`,
                globalSearchLabel: t`Bukku Store Catalogue`,
                globalSearchBaseLabel: 'Bukku Store Catalogue',
                globalSearchChildren: [
                    {
                        key: '/cp-store/categories/new',
                        label: t`New Bukku Store Category`,
                        globalSearchBaseLabel: 'New Bukku Store Category',
                    },
                ],
            },
            {
                key: '/cp-store/coupons',
                label: t`Coupons`,
                globalSearchLabel: t`Coupons`,
                globalSearchBaseLabel: 'Coupons',
                globalSearchChildren: [
                    {
                        key: '/cp-store/coupons/new',
                        label: t`New Coupon`,
                        globalSearchBaseLabel: 'New Coupon',
                    },
                ],
            },
            {
                key: '/cp-store/pages',
                label: t`Content Pages`,
                globalSearchLabel: t`Bukku Store Content Pages`,
                globalSearchBaseLabel: 'Bukku Store Content Pages',
                globalSearchChildren: [
                    {
                        key: '/cp-store/pages/new',
                        label: t`New Bukku Store Content Page`,
                        globalSearchBaseLabel: 'New Bukku Store Content Page',
                    },
                ],
            },
            {
                key: '/cp-store/settings',
                label: t`Settings`,
                globalSearchLabel: t`Bukku Store Settings`,
                globalSearchBaseLabel: 'Bukku Store Settings',
            },
        ],
        globalSearchChildren: [
            {
                key: getStoreExternalUrl('/'),
                label: t`Visit Store`,
                globalSearchBaseLabel: 'Visit Store',
                linkProps: {
                    target: '_blank',
                    rel: "noopener noreferrer",
                },
            },
        ],
    },
    {
        key: '/minipos',
        label: t`MiniPOS`,
        globalSearchBaseLabel: 'MiniPOS',
        leftIcon: 'minipos',
        isNew: true,
        children: [
            {
                key: '/minipos/settings',
                label: t`Settings`,
                globalSearchLabel: t`MiniPOS Settings`,
                globalSearchBaseLabel: 'MiniPOS Settings',
            },
            {
                key: '/minipos/orders',
                label: t`Orders`,
                globalSearchLabel: t`MiniPOS Orders`,
                globalSearchBaseLabel: 'MiniPOS Orders',
            },
        ],
    },
    {
        key: '/partners',
        label: t`Partner Portal`,
        globalSearchBaseLabel: 'Partner Portal',
        leftIcon: 'cluster',
        children: [
            {
                key: '/partners/profile',
                label: t`Partner Profile`,
                globalSearchBaseLabel: 'Partner Profile',
            },
            {
                key: '/partners/clients',
                label: t`Clients`,
                globalSearchBaseLabel: 'Clients',
            },
            {
                key: '/partners/advisors',
                label: t`Advisors`,
                globalSearchBaseLabel: 'Advisors',
                globalSearchChildren: [
                    {
                        key: '/partners/advisors/new',
                        label: t`New Advisor`,
                        globalSearchBaseLabel: 'New Advisor',
                    },
                ],
            },
            {
                key: '/partners/referrals',
                label: t`Referrals`,
                globalSearchBaseLabel: 'Referrals',
            },
            {
                key: '/partners/transactions',
                label: t`Transactions`,
                globalSearchBaseLabel: 'Transactions',
            },
            {
                key: '/partners/logs',
                label: t`Event Logs`,
                globalSearchBaseLabel: 'Event Logs',
            },
            // {
            //   key: '/partners/brand-assets',
            //   label: t`Brand Assets`,
            // },
        ],
    },
    {
        key: '/bukkuadmin',
        label: t`Bukku Admin`,
        globalSearchBaseLabel: 'Bukku Admin',
        leftIcon: 'admin',
    },
];
